import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Typography,
} from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { mutateNoBody } from '@/api/mutateNoBody'
import { truncateMiddle } from '@/components/common/MonoId'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'
import { centsToDollars, dollarsToCents, formatUsdCents } from '@/utils/money'

type ResolveReviewDecision = components['schemas']['ResolveReviewDecision']

interface Props {
  open: boolean
  intentId: string
  userId: string
  claimedAmountCents?: number | null
  onClose: () => void
}

export function ResolveReviewModal({
  open,
  intentId,
  userId,
  claimedAmountCents,
  onClose,
}: Props) {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [decision, setDecision] = useState<ResolveReviewDecision>('credit')

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const body: components['schemas']['AdminResolveReviewRequest'] = {
        decision: values.decision,
        amountCents:
          values.decision === 'credit'
            ? dollarsToCents(values.amount)
            : undefined,
        txHashes: values.txHashes ?? [],
        reason: values.reason ?? '',
      }

      await mutateNoBody(
        (parseAs) =>
          client.POST('/v2/admin/payments/intents/{id}/resolve-review', {
            params: { path: { id: intentId } },
            body,
            parseAs,
          }),
        'Failed to resolve intent',
      )
      void message.success(
        values.decision === 'credit' ? 'Intent credited' : 'Intent rejected',
      )
      form.resetFields()
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={`Resolve parked intent ${truncateMiddle(intentId)}`}
      open={open}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
      confirmLoading={loading}
      okText={decision === 'credit' ? 'Credit user' : 'Reject'}
      okButtonProps={decision === 'reject' ? { danger: true } : undefined}
      destroyOnHidden
      width={520}
    >
      <Typography.Paragraph>
        User <UserEmailLink userId={userId} /> · claimed{' '}
        {formatUsdCents(claimedAmountCents)}
      </Typography.Paragraph>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Both decisions move the intent to a terminal status, ending the review limbo."
      />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          decision: 'credit',
          amount: centsToDollars(claimedAmountCents),
        }}
        onValuesChange={(changed) => {
          if (changed.decision) setDecision(changed.decision)
        }}
      >
        <Form.Item name="decision" label="Decision">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: 'Credit', value: 'credit' },
              { label: 'Reject', value: 'reject' },
            ]}
          />
        </Form.Item>
        {decision === 'credit' && (
          <>
            <Form.Item
              name="amount"
              label="Verified amount ($)"
              rules={[{ required: true, type: 'number', min: 0.01 }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            {/* Backend rejects credit with empty txHashes: manual money needs on-chain evidence */}
            <Form.Item
              name="txHashes"
              label="Tx hashes"
              rules={[
                {
                  required: true,
                  message: 'At least one tx hash is required to credit',
                },
              ]}
            >
              <Select
                mode="tags"
                tokenSeparators={[',', ' ']}
                placeholder="On-chain tx hashes"
              />
            </Form.Item>
          </>
        )}
        <Form.Item name="reason" label="Reason">
          <Input.TextArea rows={2} placeholder="Audit note (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
